import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { 
  Trophy, Star, MapPin, Quote, Loader2, Filter, Search,
  ArrowRight, Eye, Share2, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const SpotlightsGallery = () => {
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');

  useEffect(() => {
    fetchSpotlights();
  }, [cityFilter, projectTypeFilter]);

  const fetchSpotlights = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ status: 'approved', limit: '50' });
      if (cityFilter !== 'all') params.append('city', cityFilter);
      if (projectTypeFilter !== 'all') params.append('project_type', projectTypeFilter);
      
      const response = await fetch(`${API}/api/spotlights?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSpotlights(data.spotlights || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="spotlights-gallery">
      <Header />
      
      <div className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full mb-4">
              <Trophy className="w-4 h-4" />
              Real Winners, Real Stories
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Winner Spotlights
            </h1>
            <p className="text-neutral-500 text-lg max-w-2xl mx-auto">
              See the amazing transformations and hear from real homeowners who won FREE home service giveaways
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                <SelectItem value="Toronto">Toronto</SelectItem>
                <SelectItem value="Vancouver">Vancouver</SelectItem>
                <SelectItem value="Calgary">Calgary</SelectItem>
                <SelectItem value="Ottawa">Ottawa</SelectItem>
                <SelectItem value="Montreal">Montreal</SelectItem>
              </SelectContent>
            </Select>

            <Select value={projectTypeFilter} onValueChange={setProjectTypeFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                <SelectItem value="interior_painting">Interior Painting</SelectItem>
                <SelectItem value="flooring">Flooring</SelectItem>
                <SelectItem value="bathroom_renovation">Bathroom Renovation</SelectItem>
                <SelectItem value="kitchen_cabinet_refinishing">Cabinet Refinishing</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Spotlights Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
            </div>
          ) : spotlights.length === 0 ? (
            <div className="text-center py-16">
              <Trophy className="w-16 h-16 mx-auto text-neutral-300 mb-4" />
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">No Spotlights Yet</h3>
              <p className="text-neutral-500 mb-6">
                Be the first to create a winner spotlight!
              </p>
              <Link to="/giveaways">
                <Button className="bg-[#FF3B30] hover:bg-[#DC2626]">
                  Enter Giveaways
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {spotlights.map((spotlight) => (
                <Link 
                  key={spotlight.spotlight_id}
                  to={`/spotlights/${spotlight.spotlight_id}`}
                  className="group"
                >
                  <div className="bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:border-[#FF3B30]/30 hover:shadow-lg transition-all">
                    {/* Image */}
                    <div className="relative h-48 bg-neutral-100">
                      {spotlight.after_images?.[0] ? (
                        <img 
                          src={spotlight.after_images[0]} 
                          alt={spotlight.giveaway_title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : spotlight.before_images?.[0] ? (
                        <img 
                          src={spotlight.before_images[0]} 
                          alt={spotlight.giveaway_title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-100 to-orange-100">
                          <Trophy className="w-16 h-16 text-yellow-500" />
                        </div>
                      )}
                      
                      {spotlight.status === 'featured' && (
                        <div className="absolute top-3 left-3 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full">
                          FEATURED
                        </div>
                      )}
                      
                      <div className="absolute top-3 right-3 px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                        ${spotlight.giveaway_value?.toLocaleString()}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      {/* Winner Info */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                          <Trophy className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-neutral-900">{spotlight.winner_name}</p>
                          <p className="text-sm text-neutral-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {spotlight.city}
                          </p>
                        </div>
                      </div>

                      {/* Giveaway Title */}
                      <h3 className="font-semibold text-neutral-800 mb-2 line-clamp-1">
                        {spotlight.giveaway_title}
                      </h3>

                      {/* Rating */}
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={`gallery-star-${spotlight.spotlight_id}-${i}`}
                            className={`w-4 h-4 ${i < (spotlight.rating || 5) ? 'text-yellow-500 fill-yellow-500' : 'text-neutral-300'}`}
                          />
                        ))}
                      </div>

                      {/* Testimonial Preview */}
                      {spotlight.testimonial && (
                        <p className="text-sm text-neutral-600 italic line-clamp-2 mb-3">
                          "{spotlight.testimonial}"
                        </p>
                      )}

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-xs text-neutral-400">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {spotlight.views || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Share2 className="w-3 h-3" />
                          {spotlight.shares || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-[#FF3B30] to-[#DC2626] rounded-2xl p-8 sm:p-12 text-white">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-80" />
              <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
                Want to Be Featured Here?
              </h2>
              <p className="text-white/80 mb-6 max-w-lg mx-auto">
                Enter our FREE giveaways for a chance to win amazing home services. 
                The next spotlight could be yours!
              </p>
              <Link to="/giveaways">
                <Button size="lg" className="bg-white text-[#FF3B30] hover:bg-neutral-100">
                  Browse Giveaways
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotlightsGallery;
